import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
  DialogClose,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import Image from "next/image";
import aboutus from "@/public/images/aboutus.jpg";
import timeIcon from "@/public/images/time.svg";
import voiceMessageIcon from "@/public/images/voiceMessage.svg";
import messageIcon from "@/public/images/chatLine.svg";
import phoneIcon from "@/public/images/phoneCalling.svg";
import { DayPickerProps } from "react-day-picker";
import { format, isSameDay, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useGetTimesQuery } from "@/redux/features/api/Consultation/consultationApiSlice";
import { useGetSpecificProfileQuery } from "@/redux/features/api/profile/profileApiSlice";
import MyLink from "@/components/shared/myLink/MyLink";
import PaidConsultationData from "./PaidConsultationData";
import { useTranslate } from "@/components/base/providers/TranslateProv";

const SettTime = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedTimeId, setSelectedTimeId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDateId, setSelectedDateId] = useState<number | null>(null);
  const [availableDates, setAvailableDates] = useState<
    { date: Date; id: number; isBooked: boolean }[]
  >([]);
  const [availableTimes, setAvailableTimes] = useState<
    { time: string; id: number; isBooked: boolean }[]
  >([]);

  const { data, isLoading } = useGetTimesQuery({});
  const { data: user, error } = useGetSpecificProfileQuery({});
  useEffect(() => {
    if (data?.data) {
      const extractedDates = data.data.map((item: any) => ({
        date: parseISO(item.date),
        id: item.id,
        isBooked: !item.active,
      }));
      setAvailableDates(extractedDates);
    }
  }, [data]);

  useEffect(() => {
    if (selectedDate && data?.data) {
      const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
      const selectedDateData = data.data.find(
        (item: any) => item.date === selectedDateStr
      );
      if (selectedDateData) {
        setSelectedDateId(selectedDateData.id);
        const times = selectedDateData.times.map((time: any) => ({
          time: time.time,
          id: time.id,
          isBooked: !time?.active,
        }));
        setAvailableTimes(times);
      } else {
        setAvailableTimes([]);
      }
    }
  }, [selectedDate, data]);

  const handleTimeSelect = (time: string, id: number) => {
    setSelectedTime(time);
    setSelectedTimeId(id);
  };

  const handleDateSelect: DayPickerProps["onDayClick"] = (
    day,
    { selected }
  ) => {
    setSelectedDate(selected ? null : day);
  };

  const translations = {
    en: {
      bookAppointment: "Book Appointment",
      selectDateAndTime: "Please select a suitable date and time",
      day: "Day",
      time: "Time",
      hourOnly: "1 hour only",
      videoCall: "Google Meet / Zoom / Messaging",
      phoneCall: "Phone Call",
      textMessages: "Text Messages",
      selectDay: "Select a suitable day",
      selectTime: "Select a suitable time",
      price: "399 EGP",
      discount: "30% off",
      loginFirst: "Please log in first",
      consultation: "University Consultant",
      assistance:
        "Assistance with document preparation and university applications in Egypt",
    },
    ar: {
      bookAppointment: "تحديد الموعد",
      selectDateAndTime: "قم بتحديد اليوم و الوقت المناسب معك",
      day: "اليوم",
      time: "الوقت",
      hourOnly: "ساعة فقط",
      videoCall: "جوجل ميت/ زوم / مراسلة",
      phoneCall: "اتصال هاتفي",
      textMessages: "رسائل نصية",
      selectDay: "حدد اليوم المناسب معك",
      selectTime: "حدد الساعة المناسبة معك",
      price: "399 جنية",
      discount: "خصم 30%",
      loginFirst: "سجل دخول اولا",
      consultation: "استشاره سفير الجامعات",
      assistance: "المساعدة في تجهيز المستندات المطلوبة في التقديم",
    },
  };

  const { locale }: { locale: "ar" | "en" } = useTranslate();
  const t = translations[locale];

  const today = new Date();
  const threeMonthsFromNow = new Date(
    today.getFullYear(),
    today.getMonth() + 12,
    today.getDate()
  );

  return (
    <>
      {isLoading ? (
        <div className="bg-primaryColor/10 text-primaryColor flex items-center justify-center rounded-[10px] h-[50px] w-full animate-pulse">
          {locale === "ar" ? "جاري التحميل..." : "Loading..."}
        </div>
      ) : error || !data?.data ? (
        <MyLink
          className="bg-primaryColor text-16 text-white font-[500] flex items-center justify-center rounded-[10px] h-[50px] w-full"
          href="/login"
        >
          {t.loginFirst}
        </MyLink>
      ) : availableDates.length > 0 ? (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button className="w-full" onClick={() => setIsModalOpen(true)}>
              {t.bookAppointment}
            </Button>
          </DialogTrigger>
          <DialogContent className="mx-auto max-w-4xl overflow-y-auto overflow-x-hidden max-h-screen">
            <DialogHeader>
              <DialogTitle className="text-center text-lg md:text-xl w-full">
                {t.selectDateAndTime}
              </DialogTitle>
              <DialogClose onClick={() => setIsModalOpen(false)} />
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 w-full gap-4">
              {/* Info */}
              <div className="col-span-1 md:col-span-6 lg:col-span-4 flex flex-col items-center lg:items-start text-center lg:text-start gap-4 p-4">
                <Image
                  src={aboutus}
                  alt="User Image"
                  className="w-16 h-16 rounded-full"
                />
                <h3 className="text-lg font-semibold">Dr. Nour El-Din Adel</h3>
                <ul className="text-sm flex flex-col gap-2 items-center lg:items-start text-center lg:text-start">
                  <li className="flex gap-2">
                    <Image src={timeIcon} alt="time" />
                    <span>{t.hourOnly}</span>
                  </li>
                  <li className="flex gap-2">
                    <Image src={voiceMessageIcon} alt="voiceMessage" />
                    <span>{t.videoCall}</span>
                  </li>
                  <li className="flex gap-2">
                    <Image src={phoneIcon} alt="phone" />
                    <span>{t.phoneCall}</span>
                  </li>
                  <li className="flex gap-2">
                    <Image src={messageIcon} alt="message" />
                    <span>{t.textMessages}</span>
                  </li>
                </ul>
                <div className="flex-col justify-start items-end gap-3 inline-flex">
                  <div className="text-[#6c7278] text-sm">{t.assistance}</div>
                </div>
              </div>

              {/* Date Selection */}
              <div className="w-full col-span-1 md:col-span-4 lg:col-span-5 p-4 border rounded-md flex flex-col items-center justify-center">
                <h3 className="text-lg font-semibold mb-4">{t.day}</h3>
                <p className="text-sm mb-4 pb-4 border-b">{t.selectDay}</p>

                {/* //     selected={selectedDate ? [selectedDate] : undefined}
              //     onDayClick={handleDateSelect}
              //     fromDate={availableDates[0]?.date}
              //     toDate={availableDates[availableDates.length - 1]?.date}
              //     modifiers={{
              //       disabled: [
              //         { before: availableDates[0].date },
              //         {
              //           after: availableDates[availableDates.length - 1].date,
              //         },
              //         (date: Date) =>
              //           !availableDates.some((availableDate) =>
              //             isSameDay(availableDate.date, date)
              //           ),
              //       ],
              //       blocked: (date: Date) =>
              //         availableDates.some(
              //           (availableDate) =>
              //             isSameDay(availableDate.date, date) &&
              //             availableDate.isBooked
              //         ),
              //     }}
              //     // Add custom styles for blocked dates
              //     modifiersStyles={{
              //       blocked: {
              //         backgroundColor: "#ff00006c",
              //         color: "white",
              //         border: "3px solid red",
              //       },
              //     }}
              //     classNames={{
              //       day: "  text-[700] w-9 h-9 rounded-full flex items-center justify-center border-[2px] border-[#001affdb]",
              //     }}
              //   />
              // </div> */}
                <Calendar
                  selected={selectedDate ? [selectedDate] : undefined}
                  onDayClick={handleDateSelect}
                  fromDate={availableDates[0]?.date}
                  // fromDate={today} // Start from today
                  toDate={threeMonthsFromNow} // End three months from today
                  // toDate={availableDates[availableDates.length - 1]?.date}
                  showOutsideDays // Optional: Show days from the previous or next month
                  fixedWeeks // Optional: Ensures a consistent number of weeks in the calendar view
                  pagedNavigation // Optional: Enable paged navigation if needed
                  modifiers={{
                    disabled: (date: Date) =>
                      !availableDates.some((availableDate) =>
                        isSameDay(availableDate.date, date)
                      ),
                    blocked: (date: Date) =>
                      availableDates.some(
                        (availableDate) =>
                          isSameDay(availableDate.date, date) &&
                          availableDate.isBooked
                      ),
                  }}
                  modifiersStyles={{
                    blocked: {
                      backgroundColor: "#ff00006c",
                      color: "white",
                      border: "3px solid red",
                    },
                  }}
                  classNames={{
                    day: "text-[700] w-9 h-9 rounded-full flex items-center justify-center border-[2px] border-[#001affdb]",
                  }}
                />
              </div>
              {/* Time Selection */}
              <div className="w-full col-span-1 md:col-span-2 lg:col-span-3 p-4 border rounded-md flex flex-col items-center justify-center">
                <h3 className="text-lg font-semibold mb-4">{t.time}</h3>
                <p className="text-sm mb-4 pb-4 border-b">{t.selectTime}</p>
                <div className="flex flex-col gap-2">
                  {availableTimes.map((timeSlot) => {
                    if (timeSlot?.isBooked) {
                      return (
                        <button
                          key={timeSlot.id}
                          className={`px-4 py-2 border rounded-md bg-red-100`}
                          disabled={false}
                        >
                          {timeSlot.time}
                        </button>
                      );
                    } else {
                      return (
                        <button
                          key={timeSlot.id}
                          onClick={() =>
                            handleTimeSelect(timeSlot.time, timeSlot.id)
                          }
                          className={`px-4 py-2 border rounded-md ${
                            selectedTime === timeSlot.time
                              ? "bg-primaryColor text-white"
                              : "bg-white text-black"
                          }`}
                        >
                          {timeSlot.time}
                        </button>
                      );
                    }
                  })}
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center gap-5 p-4">
              <div className="w-full">
                <PaidConsultationData
                  data={user?.data}
                  selectedDateId={selectedDateId}
                  selectedTimeId={selectedTimeId}
                >
                  <Button
                    className="grow text-lg w-full"
                    disabled={!selectedDateId || !selectedTimeId}
                  >
                    {locale === "ar"
                      ? "استشاره سفير الجامعات"
                      : "University Ambassador Consultation"}
                  </Button>
                </PaidConsultationData>
              </div>
              <div className="w-fit relative">
                <Button className="w-fit bg-[#F8F9FA] mr-2 font-semibold text-primaryColor">
                  399 {locale === "ar" ? "جنية" : "EG"}
                </Button>
                <Badge className="absolute -top-4 -left-[8px] bg-destructive rounded-full">
                  30%
                </Badge>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <p className="bg-red-500 text-16 text-white font-[500] flex items-center justify-center rounded-[10px] h-[50px] w-full">
          {locale === "ar"
            ? "لا يوجد مواعيد متاحه"
            : "No available appointments"}
        </p>
      )}
    </>
  );
};

export default SettTime;
